/**
 * Dugzi IDs in the browser (D-135): the TypeScript twin of the `dz-id` crate.
 *
 * Every ID that leaves a service is `<prefix>_<26 lowercase Crockford base32>` (TypeID-style). Web
 * apps treat IDs as opaque strings: they never decode them, they only check the shape and the prefix
 * (for example before passing an account as a sign-in hint). The prefixes come from the crate's
 * registry, copied into `ids-registry.ts` by `npm run ids` (checked by the quality gate).
 */
import { ID_PREFIXES, type IdPrefix } from './ids-registry';

export { ID_PREFIXES, type IdPrefix };

/** An ID of one kind, e.g. `Id<'usr'>` for an account. A plain string at runtime. */
export type Id<P extends IdPrefix> = string & { readonly __idPrefix: P };
/** A Dugzi account: `usr_…`. */
export type UserId = Id<'usr'>;

const SUFFIX = /^[0-7][0-9abcdefghjkmnpqrstvwxyz]{25}$/;

/** Whether `s` is an ID with this prefix, in the one canonical form (no bare UUIDs, lowercase only). */
export function isId<P extends IdPrefix>(prefix: P, s: unknown): s is Id<P> {
  return typeof s === 'string' && s.length === prefix.length + 27 && s.startsWith(`${prefix}_`) && SUFFIX.test(s.slice(prefix.length + 1));
}

/** `s` as an ID of this kind, or `null`. */
export function asId<P extends IdPrefix>(prefix: P, s: unknown): Id<P> | null {
  return isId(prefix, s) ? s : null;
}

/** The registered prefix of any Dugzi ID, or `null` if `s` isn't one. */
export function idPrefix(s: unknown): IdPrefix | null {
  if (typeof s !== 'string') return null;
  const i = s.lastIndexOf('_');
  const p = s.slice(0, i) as IdPrefix;
  return i > 0 && (ID_PREFIXES as readonly string[]).includes(p) && isId(p, s) ? p : null;
}

const ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';

/** A new UUIDv7 ID made in the browser (offline creation, D-144); the server checks it with `accept_client`. */
export function newId<P extends IdPrefix>(prefix: P, now = Date.now()): Id<P> {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  for (let i = 0; i < 6; i++) b[i] = Math.floor(now / 2 ** (8 * (5 - i))) & 0xff;
  b[6] = (b[6] & 0x0f) | 0x70; // version 7
  b[8] = (b[8] & 0x3f) | 0x80; // RFC 9562 variant
  let v = 0n;
  for (const x of b) v = (v << 8n) | BigInt(x);
  let s = '';
  for (let i = 25; i >= 0; i--) s += ALPHABET[Number((v >> BigInt(5 * i)) & 31n)];
  return `${prefix}_${s}` as Id<P>;
}
